import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { ProjectRepair } from "@/components/ProjectRepair";
import { CorrectionCascade } from "@/components/CorrectionCascade";
import { UnitsPanel } from "@/components/UnitsPanel";
import { CanonicalFindings } from "@/components/CanonicalFindings";
import { Boxes, GitMerge, Scale, ShieldCheck, ChevronRight } from "lucide-react";

const STATUS_STYLE = {
  Pass: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Attention: "bg-amber-50 text-amber-700 border-amber-200",
  Informational: "bg-indigo-50 text-indigo-700 border-indigo-200",
};
const TOTAL_STYLE = {
  Recalculated: "bg-teal-50 text-teal-700 border-teal-200",
  "Reconciled — unchanged": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "New governed total": "bg-indigo-50 text-indigo-700 border-indigo-200",
};

const Panel = ({ icon: Icon, title, subtitle, children, testid }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm shadow-slate-100" data-testid={testid}>
    <div className="flex items-start gap-2 mb-1">
      <Icon className="w-4 h-4 text-teal-600 mt-0.5" />
      <div>
        <h3 className="font-heading text-sm font-bold text-slate-900">{title}</h3>
        {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
    </div>
    <div className="mt-3">{children}</div>
  </div>
);

const linkSummary = (links) =>
  Object.entries(links || {})
    .map(([k, v]) => {
      if (Array.isArray(v)) return v.length ? `${k.replace(/_/g, " ")} (${v.length})` : null;
      return v ? `${k.replace(/_/g, " ")}: ${v}` : null;
    })
    .filter(Boolean)
    .join(" · ");

export default function InformationModel() {
  const [rec, setRec] = useState(null);
  const [objects, setObjects] = useState(null);
  const [activeType, setActiveType] = useState("source_document");

  useEffect(() => {
    api.get("/model/reconciliation").then((r) => setRec(r.data));
    api.get("/model/objects").then((r) => setObjects(r.data));
  }, []);

  if (!rec) return <div className="p-8 text-slate-400 text-sm" data-testid="model-loading">Loading governed model…</div>;

  const activeObjs = objects?.objects?.[activeType] || [];
  const activeMeta = rec.object_types.find((t) => t.type === activeType);

  return (
    <div className="space-y-5" data-testid="model-view">
      <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="model-note">
        <p className="font-mono text-[10px] uppercase tracking-widest text-teal-400 mb-2">Governed information model · v{rec.model_version}</p>
        <p className="text-sm text-slate-200 leading-relaxed max-w-4xl">{rec.note}</p>
      </div>

      <UnitsPanel />

      <CanonicalFindings />

      <ProjectRepair />

      <CorrectionCascade />

      <Panel icon={Boxes} title="Object registry" subtitle="Ten linked object types, each with a stable typed identifier space" testid="model-objects-panel">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {rec.object_types.map((t) => (
            <button key={t.type} onClick={() => setActiveType(t.type)} data-testid={`model-type-${t.type}`}
              className={`text-left rounded-lg border px-3.5 py-3 transition-colors ${activeType === t.type ? "border-teal-500 bg-teal-50/60" : "border-slate-200 bg-white hover:border-teal-300"}`}>
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-[10px] font-bold text-teal-700">{t.prefix}-</span>
                <span className="font-heading text-lg font-extrabold text-slate-900">{t.count}</span>
              </div>
              <p className="text-xs font-semibold text-slate-800 mt-1 leading-snug">{t.name}</p>
              <p className="text-[10px] text-slate-500 mt-1 leading-snug">{t.description}</p>
              <p className="font-mono text-[9px] text-slate-400 mt-1.5">{t.id_range}</p>
            </button>
          ))}
        </div>
      </Panel>

      <Panel icon={ChevronRight} title={`Object browser — ${activeMeta?.name || ""}`} subtitle="Stable identifier, legacy reference and model linkage per object" testid="model-browser-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">Stable ID</th><th className="py-2 pr-3">Legacy ref</th>
                <th className="py-2 pr-3">Object</th><th className="py-2">Links</th>
              </tr>
            </thead>
            <tbody>
              {activeObjs.map((o) => (
                <tr key={o.uid} className="border-b border-slate-100 align-top" data-testid={`model-object-${o.uid}`}>
                  <td className="py-2 pr-3 font-mono font-semibold text-teal-700 whitespace-nowrap">{o.uid}</td>
                  <td className="py-2 pr-3 font-mono text-slate-400 whitespace-nowrap">{o.legacy_id || "—"}</td>
                  <td className="py-2 pr-3 text-slate-700 max-w-md">{o.item_type ? <span className="font-mono text-[9px] uppercase tracking-wider text-indigo-600 mr-1.5">{o.item_type}</span> : null}{o.claim_type ? <span className="font-mono text-[9px] uppercase tracking-wider text-indigo-600 mr-1.5">{o.claim_type}</span> : null}{o.label}</td>
                  <td className="py-2 text-slate-500 max-w-sm">{linkSummary(o.links) || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Panel icon={GitMerge} title="Linkage matrix" subtitle="Directed relationships between object types, with link counts derived from the registry" testid="model-links-panel">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">From</th><th className="py-2 pr-3">Relation</th><th className="py-2 pr-3">To</th><th className="py-2 text-right">Links</th>
              </tr>
            </thead>
            <tbody>
              {rec.link_matrix.map((l, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 pr-3 font-semibold text-slate-800">{l.from}</td>
                  <td className="py-1.5 pr-3 text-slate-500 italic">{l.relation}</td>
                  <td className="py-1.5 pr-3 font-semibold text-slate-800">{l.to}</td>
                  <td className="py-1.5 text-right font-mono font-bold text-teal-700">{l.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel icon={ShieldCheck} title="Integrity checks" subtitle="Automated structural checks run against the registry" testid="model-integrity-panel">
          <div className="space-y-2.5">
            {rec.integrity_checks.map((c) => (
              <div key={c.id} className="border border-slate-200 rounded-lg px-3.5 py-2.5" data-testid={`model-check-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs font-semibold text-slate-800 leading-snug">{c.name}</p>
                  <Badge variant="outline" className={`text-[10px] shrink-0 ${STATUS_STYLE[c.status]}`}>{c.status}</Badge>
                </div>
                <p className="text-[11px] text-slate-500 mt-1 leading-snug">{c.detail}</p>
                {c.affected?.length > 0 && c.affected.length <= 16 && (
                  <p className="font-mono text-[9px] text-slate-400 mt-1">{c.affected.join(" · ")}</p>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <Panel icon={Scale} title="Totals reconciliation" subtitle="Every displayed total, its derivation from the model, and what it replaced" testid="model-totals-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-400 border-b border-slate-200">
                <th className="py-2 pr-3">Metric</th><th className="py-2 pr-3">Shown in</th>
                <th className="py-2 pr-3">Previously displayed</th><th className="py-2 pr-3">Recalculated value</th>
                <th className="py-2 pr-3">Derivation</th><th className="py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {rec.displayed_totals.map((t) => (
                <tr key={t.metric} className="border-b border-slate-100 align-top" data-testid={`model-total-${t.metric.toLowerCase().replace(/\s+/g, "-")}`}>
                  <td className="py-2 pr-3 font-semibold text-slate-800 whitespace-nowrap">{t.metric}</td>
                  <td className="py-2 pr-3 text-slate-500">{t.location}</td>
                  <td className="py-2 pr-3 text-slate-400 line-through decoration-slate-300 max-w-[160px]">{t.previous}</td>
                  <td className="py-2 pr-3 font-semibold text-teal-800 max-w-[200px]">{t.value}</td>
                  <td className="py-2 pr-3 text-slate-500 max-w-xs leading-snug">{t.derivation}</td>
                  <td className="py-2"><Badge variant="outline" className={`text-[10px] whitespace-nowrap ${TOTAL_STYLE[t.status] || "bg-slate-50 text-slate-600 border-slate-200"}`}>{t.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
